<template>
  <div class="category-list-container rounded-box">
    <!-- 리스트 헤더 -->
    <div class="category-list-header">
      <div class="category-item-column column-index">번호</div>
      <div class="category-item-column column-dept">진료과목</div>
      <div class="category-item-column column-name">카테고리</div>
      <div class="category-item-column column-desc">설명</div>
      <div class="category-item-column column-font">폰트 색상</div>
      <div class="category-item-column column-image">이미지</div>
      <div class="category-item-column column-cert">인증서 사용</div>
      <div class="category-item-column column-hosp">병원 예약</div>
      <div class="category-item-column column-display">노출 설정</div>
      <div class="category-item-column column-date">생성 일시</div>
      <div class="category-item-column column-manage">관리</div>
    </div>
    
    <!-- 로딩 상태 -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">데이터를 불러오는 중입니다...</p>
    </div>
    
    <!-- 검색 결과 없음 -->
    <div v-else-if="items.length === 0" class="no-result">
      검색 결과가 없습니다.
    </div>
    
    <!-- 카테고리 리스트 -->
    <draggable
      v-else
      v-model="internalItems"
      class="category-list-draggable"
      ghost-class="ghost-item"
      handle=".drag-handle"
      @end="handleDragEnd"
    >
      <template #item="{ element, index }">
        <div class="category-item">
          <div class="category-item-column column-index">
            <span class="drag-handle"><i class="fa-solid fa-grip-vertical"></i></span>
            {{ calculateIndex(index) }}
          </div>
          <div class="category-item-column column-dept">
            <span class="dept-label">{{ element.department ? MedicalDeptText[element.department] || '-' : '-' }}</span>
          </div>
          <div class="category-item-column column-name">
            <div class="name-container">
              <span class="text-truncate">{{ element.name }}</span>
              <a-tooltip v-if="hasMultiLanguageNames(element)" placement="right">
                <template #title>
                  <div v-if="element.names?.ko" class="tooltip-row">
                    <span class="tooltip-label">한국어:</span> {{ element.names.ko }}
                  </div>
                  <div v-if="element.names?.en" class="tooltip-row">
                    <span class="tooltip-label">영어:</span> {{ element.names.en }}
                  </div>
                  <div v-if="element.names?.ja" class="tooltip-row">
                    <span class="tooltip-label">일본어:</span> {{ element.names.ja }}
                  </div>
                  <div v-if="element.names?.zh" class="tooltip-row">
                    <span class="tooltip-label">중국어:</span> {{ element.names.zh }}
                  </div>
                </template>
                <span class="multilang-icon">
                  <i class="fa-solid fa-globe"></i>
                </span>
              </a-tooltip>
            </div>
          </div>
          <div class="category-item-column column-desc">
            <a-tooltip :title="element.description || '-'">
              <span class="description-text">{{ element.description || '-' }}</span>
            </a-tooltip>
          </div>
          <div class="category-item-column column-font">
            <div class="color-preview">
              <div>
                <div class="color-box" :style="{ backgroundColor: element.activeFontColor || '#000000' }"></div>
                <span class="text-small">활성</span>
              </div>
              <div>
                <div class="color-box" :style="{ backgroundColor: element.inactiveFontColor || '#999999' }"></div>
                <span class="text-small">비활성</span>
              </div>
            </div>
          </div>
          <div class="category-item-column column-image">
            <div class="image-preview">
              <div class="image-container">
                <img v-if="element.activeImage" :src="element.activeImage" alt="활성 이미지" />
                <div v-else class="empty-image"></div>
                <div class="image-label">활성</div>
              </div>
              <div class="image-container">
                <img v-if="element.inactiveImage" :src="element.inactiveImage" alt="비활성 이미지" />
                <div v-else class="empty-image"></div>
                <div class="image-label">비활성</div>
              </div>
            </div>
          </div>
          <div class="category-item-column column-cert">
            <span class="status-badge" :class="{ 'status-active': element.useCertificate, 'status-inactive': !element.useCertificate }">
              {{ element.useCertificate ? '사용' : '미사용' }}
            </span>
          </div>
          <div class="category-item-column column-hosp">
            <span class="status-badge" :class="{ 'status-active': element.useHospital, 'status-inactive': !element.useHospital }">
              {{ element.useHospital ? '사용' : '미사용' }}
            </span>
          </div>
          <div class="category-item-column column-display">
            <span class="status-badge" :class="getCompanyModeClass(element.company?.mode)">
              {{ getCompanyModeText(element.company?.mode) }}
            </span>
          </div>
          <div class="category-item-column column-date">
            {{ convertDate(element.createdAt) }}
          </div>
          <div class="category-item-column column-manage">
            <div class="button-group">
              <button class="table-action-btn btn-outline-primary" @click="handleEdit(element)">
                <i class="fa-solid fa-pen-to-square"></i> 수정
              </button>
              <button class="table-action-btn btn-outline-danger" @click="handleDelete(element)">
                <i class="fa-solid fa-trash"></i> 삭제
              </button>
            </div>
          </div>
        </div>
      </template>
    </draggable>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import draggable from 'vuedraggable';
import { convertDate } from "@/utils/utils";
import { MedicalDeptText } from "@/utils/enums";

export default defineComponent({
  name: "CategoryTable",
  components: {
    draggable
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    rowsPerPage: {
      type: Number,
      default: 10
    }
  },
  emits: ['update:items', 'drag-end', 'edit', 'delete'],
  data() {
    return {
      MedicalDeptText
    };
  },
  computed: {
    internalItems: {
      get() {
        return this.items;
      },
      set(newVal) {
        this.$emit('update:items', newVal);
      }
    }
  },
  methods: {
    convertDate,
    
    /**
     * 다국어 이름 존재 여부 체크
     */
    hasMultiLanguageNames(category) {
      return category.names && (
        category.names.ko || category.names.en || category.names.ja || category.names.zh
      );
    },
    
    /**
     * 회사 노출 모드 텍스트 반환
     */
    getCompanyModeText(mode) {
      switch(mode) {
        case 'all': return '전부노출';
        case 'positive': return '선택 병원만 노출';
        case 'negative': return '선택 병원 제외';
        default: return '그룹 병원 관리';
      }
    },
    
    /**
     * 회사 노출 모드 클래스 반환
     */
    getCompanyModeClass(mode) {
      switch(mode) {
        case 'all': return 'display-all';
        case 'positive': return 'display-part';
        case 'negative': return 'display-exclude';
        default: return 'display-other';
      }
    },
    
    /**
     * 번호 계산 (페이지네이션 반영)
     */
    calculateIndex(index) {
      return (this.currentPage - 1) * this.rowsPerPage + index + 1;
    },
    
    /**
     * 드래그앤드롭 이벤트 처리
     */
    handleDragEnd(event) {
      this.$emit('drag-end', {
        newIndex: event.newIndex,
        oldIndex: event.oldIndex,
        item: this.internalItems[event.newIndex]
      });
    },
    
    /**
     * 수정 버튼 클릭 이벤트 처리
     */
    handleEdit(item) {
      this.$emit('edit', item);
    },
    
    /**
     * 삭제 버튼 클릭 이벤트 처리
     */
    handleDelete(item) {
      this.$emit('delete', item);
    }
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';
@import '../styles/common.scss';

/* 카테고리 리스트 스타일 */
.category-list-container {
  margin-bottom: $spacing-lg;
  box-shadow: $box-shadow;
  
  .category-list-header {
    display: flex;
    background-color: $bg-light;
    border-bottom: 1px solid $border-color;
    font-weight: $font-weight-bold;
    
    .category-item-column {
      padding: $spacing-md $spacing-sm;
      text-align: center;
    }
  }
  
  .category-list-draggable {
    min-height: 100px;
  }
  
  .category-item {
    display: flex;
    border-bottom: 1px solid $border-color;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: $bg-light-hover;
    }
    
    &:last-child {
      border-bottom: none;
    }
    
    .category-item-column {
      padding: $spacing-sm;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  
  .column-index {
    flex: 0 0 60px;
    
    .drag-handle {
      cursor: grab;
      margin-right: $spacing-xs;
      color: $secondary-color;
    }
  }
  
  .column-dept { 
    flex: 0 0 100px; 
    
    .dept-label {
      background-color: $bg-light;
      padding: $spacing-xs $spacing-sm;
      border-radius: $border-radius-sm;
      font-size: $font-size-sm;
    }
  }
  
  .column-name { 
    flex: 0 0 150px; 
    
    .name-container {
      display: flex;
      align-items: center;
      width: 100%;
    }
    
    .multilang-icon {
      margin-left: $spacing-xs;
      font-size: $font-size-sm;
      color: $primary-color;
      cursor: pointer;
    }
  }
  
  .column-desc { 
    flex: 1; 
    
    .description-text {
      max-width: 100%;
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 200px; /* 텍스트 최대 너비 지정 */
    }
  }
  
  .column-font { flex: 0 0 120px; }
  .column-image { flex: 0 0 200px; }
  .column-cert, .column-hosp { flex: 0 0 100px; }
  .column-display { flex: 0 0 100px; }
  .column-date { flex: 0 0 150px; }
  .column-manage { flex: 0 0 150px; }
  
  /* 색상 미리보기 */
  .color-preview {
    display: flex;
    gap: $spacing-sm;
    justify-content: center;
    
    .color-box {
      width: 20px;
      height: 20px;
      border-radius: $border-radius-sm;
      display: inline-block;
      margin-right: $spacing-xs;
      border: 1px solid $border-color-dark;
    }
  }
  
  /* 이미지 미리보기 */
  .image-preview {
    display: flex;
    gap: $spacing-md;
    justify-content: center;
    
    .image-container {
      text-align: center;
      
      img, .empty-image {
        width: 50px;
        height: 50px;
        object-fit: contain;
        border-radius: $border-radius-sm;
        border: 1px solid $border-color;
      }
      
      .empty-image {
        background-color: $bg-light;
        display: inline-block;
      }
      
      .image-label {
        font-size: $font-size-xs;
        margin-top: $spacing-xs;
        color: $text-muted;
      }
    }
  }
}

/* 툴팁 스타일 */
.tooltip-row {
  margin-bottom: $spacing-xs;
  white-space: nowrap;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .tooltip-label {
    font-weight: $font-weight-bold;
    margin-right: $spacing-xs;
  }
}

/* 설명 툴팁 스타일 */
:deep(.ant-tooltip-inner) {
  max-width: 500px;
  white-space: normal;
  word-break: break-word;
  text-align: left;
}

/* 상태 배지 스타일 */
.status-badge {
  display: inline-block;
  padding: $spacing-xs $spacing-sm;
  border-radius: $border-radius-sm;
  font-size: $font-size-xs;
  font-weight: $font-weight-medium;
  
  &.status-active {
    background-color: $success-color;
    color: $bg-white;
  }
  
  &.status-inactive {
    background-color: $secondary-color;
    color: $bg-white;
  }
  
  &.display-all {
    background-color: $success-color;
    color: $bg-white;
  }
  
  &.display-part {
    background-color: $primary-color;
    color: $bg-white;
  }
  
  &.display-exclude {
    background-color: $warning-color;
    color: $text-dark;
  }
  
  &.display-other {
    background-color: $secondary-color;
    color: $bg-white;
  }
}

/* 버튼 그룹 스타일 */
.button-group {
  display: flex;
  gap: $spacing-xs;
  
  .table-action-btn {
    padding: $spacing-xs $spacing-sm;
    font-size: $font-size-xs;
    border-radius: $border-radius-sm;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    
    i {
      margin-right: $spacing-xs;
    }
    
    &.btn-outline-primary {
      &:hover {
        background-color: $primary-color;
        color: white !important;
      }
      
      &[disabled] {
        opacity: 0.6;
        cursor: not-allowed;
        border-color: $secondary-color;
        color: $secondary-color;
        
        &:hover {
          background-color: transparent;
          color: $secondary-color !important;
        }
      }
    }
    
    &.btn-outline-danger {
      &:hover {
        background-color: $danger-color;
        color: white !important;
      }
    }
  }
}

/* 드래그앤드롭 스타일 */
.ghost-item {
  opacity: 0.5;
  background: #f0f8ff;
  border: 1px dashed $primary-color;
}

/* 반응형 디자인 */
@media (max-width: 1200px) {
  .category-list-container {
    overflow-x: auto;
    
    .category-list-header,
    .category-item {
      min-width: 1100px; // 최소 너비 증가
    }
  }
}

@media (max-width: 768px) {
  .column-image { 
    display: none; // 모바일에서는 이미지 컬럼 숨김
  }
  
  .column-font { 
    display: none; // 모바일에서는 폰트 컬럼 숨김
  }
  
  .category-list-header,
  .category-item {
    min-width: 900px; // 최소 너비 재조정
  }
}
</style> 